/* ===========================================
   UTM → Cart Attributes (Global Sync)
   - Captures URL params (UTMs + custom keys)
   - Stores in cookies (first-touch or last-touch)
   - Syncs into Shopify cart attributes (flows to order)
   - Works on page load, form submits, button clicks, AJAX /cart/add.js
   - Dedupe to avoid extra network calls
   - No-regex cookie helpers (fixes "Unterminated group" error)
   =========================================== */

(() => {
  // ======= CONFIG =======
  const PARAM_KEYS = [
    // Standard UTMs
    'utm_source','utm_medium','utm_campaign','utm_term','utm_content',
    // Common click IDs
    'gclid','fbclid','msclkid','ttclid',
    // Your custom params
    'source','sv1','sv_campaign_id','awc','sn'
  ];

  // Store full landing querystring (optional)
  const ATTR_NAME_FULL_QUERY = 'landing_query';

  // Cookie lifetime (days)
  const COOKIE_DAYS   = 30;

  // Attribution model: 'first' (keep first values) or 'last' (overwrite with latest)
  const TOUCH_MODEL   = 'last';

  // If you want cross-subdomain cookies (e.g., apex + www), set to your root domain including leading dot:
  // Example: '.smilewhite.co.uk' — leave '' to use current host only.
  const COOKIE_DOMAIN = '';

  // Debug logging
  const DEBUG         = false;

  // ======= HELPERS =======
  const log = (...a) => { if (DEBUG) console.log('[utm-sync]', ...a); };

  // No-regex cookie helpers (robust even with '=' in values)
  function setCookie(name, value, days) {
    const d = new Date();
    d.setTime(d.getTime() + days * 864e5);
    const parts = [
      `${encodeURIComponent(name)}=${encodeURIComponent(value)}`,
      `expires=${d.toUTCString()}`,
      'path=/',
      'SameSite=Lax'
    ];
    if (location.protocol === 'https:') parts.push('Secure');
    if (COOKIE_DOMAIN) parts.push(`Domain=${COOKIE_DOMAIN}`);
    document.cookie = parts.join('; ');
  }

  function getCookie(name) {
    const all = document.cookie ? document.cookie.split('; ') : [];
    for (const part of all) {
      const idx = part.indexOf('=');
      if (idx === -1) continue;
      const k = decodeURIComponent(part.slice(0, idx));
      if (k === name) {
        return decodeURIComponent(part.slice(idx + 1));
      }
    }
    return '';
  }

  function chooseValue(key, urlParams) {
    const urlVal = urlParams.get(key);
    const cookieVal = getCookie(key);
    if (TOUCH_MODEL === 'first') {
      // First-touch: keep cookie if present, else take URL
      return cookieVal || urlVal || '';
    } else {
      // Last-touch: URL wins if present, else cookie
      return (urlVal || cookieVal || '');
    }
  }

  function collectParams() {
    const qs = new URLSearchParams(location.search);
    const out = {};

    // Pick & persist keys
    PARAM_KEYS.forEach(k => {
      const val = chooseValue(k, qs);
      if (val) {
        setCookie(k, val, COOKIE_DAYS);  // persist per model
        out[k] = val;
      }
    });

    // Full querystring (optional)
    const currentQ = location.search ? location.search.slice(1) : '';
    if (currentQ) setCookie(ATTR_NAME_FULL_QUERY, currentQ, COOKIE_DAYS);
    const savedQ = getCookie(ATTR_NAME_FULL_QUERY) || currentQ;
    if (savedQ) out[ATTR_NAME_FULL_QUERY] = savedQ;

    return out;
  }

  // Cheap stable hash to dedupe payloads
  function hashObj(obj) {
    try { return btoa(unescape(encodeURIComponent(JSON.stringify(obj)))); }
    catch { return String(Date.now()); }
  }

  let lastHash = null;

  async function syncAttributes(reason = 'auto') {
    try {
      const attrs = collectParams();
      if (!Object.keys(attrs).length) { log('no attrs'); return; }

      const h = hashObj(attrs);
      const prev = localStorage.getItem('utm_sync_hash_v1');
      if (h === lastHash || h === prev) { log('dedupe', reason); return; }

      log('POST /cart/update.js', { reason, attrs });
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ attributes: attrs })
      });
      if (!res.ok) throw new Error('update.js failed: ' + res.status);

      lastHash = h;
      localStorage.setItem('utm_sync_hash_v1', h);
      log('synced', reason);
    } catch (e) {
      console.warn('[utm-sync] error', e);
    }
  }

  // ======= TRIGGERS (broad coverage) =======

  // 0) Run ASAP on script load (creates cart session early if needed)
  syncAttributes('script-load');

  // 1) DOM ready (some themes inject forms/buttons after initial HTML)
  document.addEventListener('DOMContentLoaded', () => syncAttributes('domcontentloaded'));

  // 2) Classic add-to-cart form submit
  window.addEventListener('submit', (e) => {
    const f = e.target;
    if (f && f.matches('form[action="/cart/add"], form[action^="/cart/add"]')) {
      syncAttributes('form-submit');
    }
  }, true);

  // 3) Common add-to-cart buttons (covers many themes/quick-adds)
  window.addEventListener('click', (e) => {
    const t = e.target && e.target.closest
      ? e.target.closest(
          'button[name="add"],' +
          'button[type="submit"][form][formaction*="/cart/add"],' +
          '[data-add-to-cart],' +
          '.product-form__submit,' +
          '.quick-add__submit,' +
          '[data-product-add]'
        )
      : null;
    if (t) syncAttributes('button-click');
  }, true);

  // 4) Intercept AJAX /cart/add.js via fetch
  const _fetch = window.fetch;
  window.fetch = function(url, opts, ...rest) {
    try {
      const u = typeof url === 'string' ? url : (url && url.url);
      if (u && /\/cart\/add\.js(\?|$)/.test(u)) syncAttributes('fetch-intercept');
    } catch {}
    return _fetch.call(this, url, opts, ...rest);
  };

  // 5) Intercept AJAX /cart/add.js via XHR
  const _open = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function(method, url, ...rest) {
    try {
      if (typeof url === 'string' && /\/cart\/add\.js(\?|$)/.test(url)) {
        this.addEventListener('loadstart', () => syncAttributes('xhr-intercept'));
      }
    } catch {}
    return _open.call(this, method, url, ...rest);
  };

  // 6) Theme custom events (optional, harmless if unused)
  document.addEventListener('cart:updated', () => syncAttributes('cart-updated-event'));
  document.addEventListener('product:added', () => syncAttributes('product-added-event'));

  // 7) Page show (bfcache) & tab focus
  window.addEventListener('pageshow', () => syncAttributes('pageshow'));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') syncAttributes('visible');
  });

})();
